'use client'

import { useMemo, useState } from 'react'

import dynamic from 'next/dynamic'

import { JobPageItem, TermsConditions } from '@types'

import { match } from 'ts-pattern'

import { AppcastDisclaimer, GenericDisclaimer } from '@components/job/apply-disclaimer/disclaimers'
import withChat from '@hocs/withChat'

import styles from './apply-disclaimer.module.scss'
import Link from 'next/link'
import useLocale from '@hooks/client/useLocale'

export type ApplyDisclaimerProps = {
  termsConditions: TermsConditions[]
  jobSource: JobPageItem['source']
  isLinkedin?: boolean
  isEligibleToApply: boolean
}

const TermsAndConditionsModal = dynamic(() => import('@components/popup/terms-and-conditions-modal'), { ssr: false })

function ApplyDisclaimer({
  termsConditions,
  jobSource,
  isLinkedin,
  isEligibleToApply,
}: ApplyDisclaimerProps): JSX.Element {
  const showDisclaimer = useMemo(() => termsConditions.length > 0, [termsConditions])
  const [url, setUrl] = useState<string>('')
  const [showTC, setShowTC] = useState<boolean>(false)
  const country = useLocale()

  function showTermsAndConditions(termsAndConditionsUrl?: string): void {
    setShowTC(false)
    setShowTC(true)
    setUrl(termsAndConditionsUrl || '')
  }
  const { sourceList, components }: { sourceList: string; components: JSX.Element[] } = useMemo(() => {
    let sl = ''
    const ct = []
    for (let i = 0; i < termsConditions.length; i += 1) {
      const termsCondition = termsConditions[i]
      sl += termsConditions[i].provider
      ct.push(<span key={`prov_${i}`}>{`${termsCondition.provider}'s `}</span>)
      for (let j = 0; j < termsCondition.documents.length; j += 1) {
        const doc = termsCondition.documents[j]
        ct.push(
          <span className={styles.link} onClick={(): void => showTermsAndConditions(doc.url)} key={`doc_${i}_${j}`}>
            {doc.title}
          </span>,
        )
        if (j === termsCondition.documents.length - 2) {
          ct.push(<span key={`and_${i}_${j}`}> and </span>)
        } else if (j <= termsCondition.documents.length - 3) {
          ct.push(<span key={`and_${i}_${j}`}>/</span>)
        }
      }
      if (i === termsConditions.length - 2) {
        sl += ' and '
        ct.push(<span key={`and_${i}`}> and </span>)
      } else if (i <= termsConditions.length - 3) {
        ct.push(<span key={`and_${i}`}>, </span>)
        sl += ', '
      }
    }
    return { sourceList: sl, components: ct }
  }, [termsConditions])

  if (showDisclaimer) {
    if (isEligibleToApply && !isLinkedin) {
      const disclaimer = match(jobSource)
        .with('APPCAST_EASY_APPLY', () => <AppcastDisclaimer className={styles.custom} />)
        .with('', () => <></>)
        .otherwise(() => (
          <GenericDisclaimer source={sourceList} className={styles.generic}>
            {components}
          </GenericDisclaimer>
        ))

      return (
        <>
          <TermsAndConditionsModal show={showTC} setShow={setShowTC} url={url} />
          {disclaimer}
          <p className={styles.additional}>
            SonicJobs&apos;{' '}
            <Link target="_blank" href={`/${country}/terms-conditions`}>
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link target="_blank" href={`/${country}/privacy`}>
              Privacy Policy
            </Link>{' '}
            also apply.
          </p>
        </>
      )
    }
    if (!isEligibleToApply) {
      return <p className={styles['not-eligible']}>This job is not available in your country</p>
    }
  }
  return <></>
}

// Necessary in order to show the terms and condition modal back button
export default withChat(ApplyDisclaimer)
